import { cx } from '@flexbyt/widget-preview';
import { Link } from '@tanstack/react-router';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * Buttons and links.
 *
 * ---------------------------------------------------------------------------
 * The primary action is the brand accent
 * ---------------------------------------------------------------------------
 * This reverses an earlier decision, and the reasoning for that decision is
 * worth keeping, because it was not wrong — it was answering a different
 * question.
 *
 * The argument for a near-black primary was that `blue-700` *plus a pill* is
 * the most recognisable "generated SaaS page" signal there is, and that ink
 * matches the logo. Both hold. But near-black also made the primary action
 * indistinguishable in weight from every other dark thing on the page — the
 * heading, the ink band, the dark panel in a diagram — on a site that is
 * otherwise white and grey. The page had one colour decision and spent it on
 * stars.
 *
 * So the accent now carries the primary action. What kept the template look
 * away was never the hue on its own; it was the hue *and* the pill *and* the
 * gradient *and* the glow. The radius stays at `rounded-lg` (see below), there
 * is no gradient, and the shadow is a single tight step — so this reads as a
 * deliberate brand colour rather than as a starter template.
 *
 * `accent-600` is the fill because the token block designates it "anything with
 * words in it", i.e. the step that has to clear 4.5:1. White on it is ~7.6:1.
 * `accent-700` is the hover step, which is what that slot exists for.
 *
 * Gold is still not in here at all; it belongs to stars.
 *
 * ---------------------------------------------------------------------------
 * The radius is small on purpose
 * ---------------------------------------------------------------------------
 * `rounded-lg` — 10px — not `rounded-full`. A pill reads as friendly and
 * informal, which is the right register for a consumer app and the wrong one
 * for "this is where your business's reputation lives". It is also the shape
 * the dashboard's own buttons use, and the two products should not feel
 * different at the moment somebody crosses between them.
 */

const VARIANTS = {
  /**
   * The one action a page most wants taken. Rarely more than one per screen.
   * The shadow is a single tight step — enough to lift it off the page, far
   * short of the drop shadow that makes a button look like it is hovering
   * three inches above the document.
   */
  primary: cx(
    'bg-ink-950 text-white shadow-xs',
    'hover:bg-ink-800 active:bg-ink-800'
  ),
  /**
   * The action beside the primary one. White with a hairline, because a second
   * filled button turns a pair of choices into a decision.
   */
  secondary: cx(
    'bg-white/70 text-ink-950 ring-1 ring-inset ring-ink-200 backdrop-blur',
    'hover:bg-surface hover:ring-ink-300 active:bg-surface'
  ),
  /**
   * No surface until it is pointed at. For navigation and toolbars, where a row
   * of bordered buttons is a row of boxes.
   */
  ghost: cx('text-ink-700 hover:bg-ink-50 hover:text-ink-950 active:bg-ink-100'),
  /**
   * The primary action on an ink band.
   *
   * White rather than the accent, even though the accent is now the primary
   * fill elsewhere. `accent-600` on `ink-950` is around 2:1 — a blue button on
   * a near-black band is a button you have to look for. White is the honest
   * inversion of "the heaviest thing on this surface", which is what the
   * primary action is in both places.
   */
  inverse: cx('bg-white text-ink-950', 'hover:bg-ink-100 active:bg-ink-200'),
  /**
   * The secondary action on an ink band.
   */
  'inverse-ghost': cx(
    'bg-white/10 text-white ring-1 ring-inset ring-white/15',
    'hover:bg-white/15 hover:ring-white/25'
  ),
} as const;

const SIZES = {
  sm: 'h-9 px-3.5 text-body-sm gap-1.5',
  /* 44px. The comfortable target size on a touch screen, and the size the hero
     call to action wants anyway. */
  md: 'h-11 px-5 text-body gap-2',
  lg: 'h-13 px-7 text-body gap-2',
} as const;

export type ButtonVariant = keyof typeof VARIANTS;
export type ButtonSize = keyof typeof SIZES;

/**
 * The recipe on its own.
 *
 * Exported because some actions have to be real anchors — "Start free" points
 * at a different origin, which is a link and not a button, and a `<button>` with
 * an onClick that assigns `location.href` breaks middle-click, right-click,
 * "open in new tab", and every browser affordance a link has.
 */
export function buttonClass({
  variant = 'primary',
  size = 'md',
  block = false,
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  className?: string;
} = {}): string {
  return cx(
    'inline-flex shrink-0 items-center justify-center rounded-full font-semibold whitespace-nowrap',
    'transition-[color,background-color,box-shadow,translate,scale] duration-[var(--duration-fast)]',
    /* A button that answers the hand: it lifts a pixel under the pointer,
       gives under the press, and a trailing arrow leans the way it points. */
    'hover:-translate-y-px active:translate-y-0 active:scale-[0.97]',
    '[&>svg:last-child]:transition-transform [&>svg:last-child]:duration-[var(--duration-base)] hover:[&>svg:last-child]:translate-x-0.5',
    'disabled:pointer-events-none disabled:opacity-50',
    VARIANTS[variant],
    SIZES[size],
    block && 'w-full',
    className
  );
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
};

export function Button({
  variant,
  size,
  block,
  className,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={buttonClass({ variant, size, block, className })}
      {...props}
    />
  );
}

/**
 * A button-shaped link to somewhere on this site.
 *
 * A real `<a>` via the router, so preloading on hover works and the browser's
 * link affordances survive.
 */
export function ButtonLink({
  to,
  variant,
  size,
  block,
  className,
  children,
  ...props
}: {
  to: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  className?: string;
  children: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  return (
    <Link
      to={to}
      className={buttonClass({ variant, size, block, className })}
      {...props}
    >
      {children}
    </Link>
  );
}

/**
 * A button-shaped link to another origin — the dashboard, the Chrome Web Store,
 * a platform's app marketplace.
 *
 * Separate from `ButtonLink` because the router cannot navigate to these and
 * would try. `rel="noreferrer"` rides along with any `target="_blank"`: without
 * it the opened page gets a handle on this one through `window.opener`.
 */
export function ButtonAnchor({
  href,
  variant,
  size,
  block,
  className,
  children,
  target,
  ...props
}: {
  href: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  className?: string;
  children: ReactNode;
} & AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a
      href={href}
      target={target}
      rel={target === '_blank' ? 'noreferrer' : undefined}
      className={buttonClass({ variant, size, block, className })}
      {...props}
    >
      {children}
    </a>
  );
}

/**
 * A button-shaped link to a section of the page it is already on.
 */
export function ButtonHashLink({
  hash,
  variant,
  size,
  className,
  children,
  ...props
}: {
  hash: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  /*
    A plain fragment anchor, not a router navigation.

    The target is on this page. Routing to it would re-run the route, push a
    history entry and then scroll — where the browser does the scroll natively,
    honours `scroll-padding-block-start` so the heading clears the sticky
    header, and works before any JavaScript has loaded.
  */
  return (
    <a
      href={`#${hash}`}
      className={buttonClass({ variant, size, className })}
      {...props}
    >
      {children}
    </a>
  );
}

/**
 * An interactive run of text.
 *
 * Underlined, always. An unmarked colour-only link fails for anybody who cannot
 * separate the two hues, and on this site's palette a link is competing with
 * near-black body copy rather than standing out from grey.
 *
 * The underline starts light and firms up on hover, which is a small piece of
 * motion that explains something: the link was always there, and now it is the
 * one you are pointing at.
 */
export const linkClass = cx(
  'font-medium text-accent-600 underline decoration-accent-200 decoration-1 underline-offset-[3px]',
  'transition-colors duration-[var(--duration-fast)]',
  'hover:text-accent-700 hover:decoration-accent-600'
);

/**
 * The quieter link, for navigation and footers.
 *
 * Ink rather than accent, and the underline appears on hover instead of sitting
 * there. In a footer column of nine links, nine permanent underlines is a
 * texture rather than a set of choices — the surrounding list already says
 * these are links.
 */
export const navLinkClass = cx(
  'text-ink-600 underline-offset-[3px]',
  'transition-colors duration-[var(--duration-fast)]',
  'hover:text-ink-950 hover:underline'
);

/**
 * A link to a path this application may not have built yet.
 *
 * ---------------------------------------------------------------------------
 * Why this exists rather than a cast at each call site
 * ---------------------------------------------------------------------------
 * TanStack Router types `to` against the routes that actually exist, which is
 * a genuinely useful guarantee — it catches a typo in `/pricing` at build time.
 * It also means the navigation and the footer, which name the whole planned
 * site, cannot be written until every page is built.
 *
 * The site map is the plan; the pages arrive over several phases. So paths go
 * through here, in one place, with the widening visible and explained rather
 * than scattered as `as never` casts that read like somebody fighting the
 * compiler. Until a route lands, the link reaches the 404 page — which lists
 * the places people were most likely heading.
 *
 * When every route exists this can be deleted and the call sites will typecheck
 * against the real union.
 */
export function SiteLink({
  to,
  className,
  children,
  ...props
}: {
  to: string;
  className?: string;
  children: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  return (
    <Link to={to} className={className} {...props}>
      {children}
    </Link>
  );
}

export function TextLink({
  to,
  className,
  children,
  ...props
}: {
  to: string;
  className?: string;
  children: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  return (
    <Link to={to} className={cx(linkClass, className)} {...props}>
      {children}
    </Link>
  );
}
